import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const DraftEvolutionView = ({ kinId, currentStatus }) => {
  const { user } = useAuth();
  const [evolution, setEvolution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (kinId) {
      fetchEvolution();
    }
  }, [kinId]);

  const fetchEvolution = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/member-drafts/my/${kinId}/evolution`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEvolution(data);
      } else {
        const err = await res.json();
        setError(err.error || 'Failed to load evolution');
      }
    } catch (err) {
      console.error('Error fetching evolution:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-500">Loading draft evolution...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600">Error: {error}</div>
      </div>
    );
  }

  if (!evolution || !evolution.versions || Object.keys(evolution.versions).length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-500">No evolution data available yet. Complete your draft to see its evolution.</div>
      </div>
    );
  }

  const versions = ['v0', 'v1', 'v2', 'v3'];
  const versionLabels = {
    v0: { label: 'Retail Baseline', icon: '🏪', description: 'Supermarket prices' },
    v1: { label: 'Member Optimized', icon: '👤', description: 'Your volume discounts' },
    v2: { label: 'Group Optimized', icon: '👥', description: 'Kin group discounts' },
    v3: { label: 'Final Order', icon: '✅', description: 'Your final order' }
  };
  const versionColors = {
    v0: 'bg-gray-50 border-gray-200',
    v1: 'bg-blue-50 border-blue-200',
    v2: 'bg-green-50 border-green-200',
    v3: 'bg-purple-50 border-purple-200'
  };
  const versionTextColors = {
    v0: 'text-gray-600',
    v1: 'text-blue-700',
    v2: 'text-green-700',
    v3: 'text-purple-700'
  };

  // Find the latest version that exists
  const existingVersions = versions.filter(v => evolution.versions[v] !== null);
  const latestVersion = existingVersions[existingVersions.length - 1];

  // Get stage descriptions
  const stageDesc = evolution.stageDescriptions || {};

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[800px]">
        {/* Current Status Banner */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-between">
          <div>
            <span className="font-medium">Current Status: </span>
            <span className={`px-2 py-1 rounded text-sm ${
              currentStatus === 'draft' ? 'bg-gray-200 text-gray-700' :
              currentStatus === 'submitted' ? 'bg-yellow-100 text-yellow-800' :
              currentStatus === 'consolidated' ? 'bg-green-100 text-green-800' :
              currentStatus === 'revising' ? 'bg-blue-100 text-blue-800' :
              'bg-purple-100 text-purple-800'
            }`}>
              {stageDesc[currentStatus]?.icon || '📝'} {currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1)}
            </span>
          </div>
          <div className="text-sm text-gray-500">
            {stageDesc[currentStatus]?.description || ''}
          </div>
        </div>

        {/* 4-Column Grid */}
        <div className="grid grid-cols-4 gap-3">
          {versions.map(version => {
            const data = evolution.versions[version];
            const isLatest = version === latestVersion;
            
            if (!data) {
              return (
                <div key={version} className={`${versionColors[version]} border rounded-lg p-3 opacity-50`}>
                  <div className="flex items-center gap-1 mb-1">
                    <span>{versionLabels[version].icon}</span>
                    <p className="text-sm font-medium text-gray-400">{versionLabels[version].label}</p>
                  </div>
                  <p className="text-xs text-gray-400">Not yet available</p>
                  <p className="text-xs text-gray-400 mt-2">—</p>
                </div>
              );
            }

            const isLocked = data.isLocked !== undefined ? data.isLocked : true;

            return (
              <div key={version} className={`${versionColors[version]} border rounded-lg p-3 relative ${isLatest ? 'ring-2 ring-blue-400 shadow-md' : ''}`}>
                {/* Header */}
                <div className="flex justify-between items-start mb-1">
                  <div className="flex items-center gap-1">
                    <span>{versionLabels[version].icon}</span>
                    <p className={`text-sm font-medium ${versionTextColors[version]}`}>
                      {versionLabels[version].label}
                    </p>
                  </div>
                  {isLatest && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Current</span>
                  )}
                  {isLocked && version !== 'v3' && (
                    <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">🔒</span>
                  )}
                  {!isLocked && version === 'v3' && (
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">✏️ Editing</span>
                  )}
                </div>

                {/* Total */}
                <p className={`text-xl font-bold ${versionTextColors[version]}`}>
                  ${data.total?.toFixed(2) || '0.00'}
                </p>

                {/* Savings */}
                {data.savings > 0 && (
                  <p className="text-xs text-green-600 font-medium">
                    💰 Saved ${data.savings.toFixed(2)}
                  </p>
                )}

                {/* Items count */}
                <p className="text-xs text-gray-500 mt-1">
                  {data.itemCount || data.items?.length || 0} items
                </p>

                {/* Timestamp */}
                {data.timestamp && (
                  <p className="text-xs text-gray-400 mt-1">
                    ⏱️ {new Date(data.timestamp).toLocaleString()}
                  </p>
                )}

                {/* Item preview */}
                {data.items && data.items.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <div className="space-y-0.5 max-h-32 overflow-y-auto">
                      {data.items.slice(0, 4).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-xs">
                          <span className="text-gray-600 truncate max-w-[60px]">{item.productName}</span>
                          <span className="text-gray-600">×{item.quantity}</span>
                          <span className="font-medium text-gray-700">${item.totalPrice?.toFixed(2)}</span>
                        </div>
                      ))}
                      {data.items.length > 4 && (
                        <div className="text-xs text-gray-400">+{data.items.length - 4} more</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Version label */}
                <p className="text-xs text-gray-400 mt-2">
                  {data.reason || versionLabels[version].description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Summary Bar */}
        {evolution.summary && evolution.summary.totalSavings > 0 && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4 grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-sm text-green-700">💰 Total Savings</p>
              <p className="text-xl font-bold text-green-700">
                ${evolution.summary.totalSavings?.toFixed(2)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-green-700">📊 Savings Rate</p>
              <p className="text-xl font-bold text-green-700">
                {evolution.summary.savingsPercent?.toFixed(1)}%
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-green-700">👥 Kin Total Savings</p>
              <p className="text-xl font-bold text-green-700">
                ${evolution.summary.kinTotalSavings?.toFixed(2) || '0.00'}
              </p>
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="mt-3 text-xs text-gray-400 flex flex-wrap gap-3">
          <span>🔒 = Locked (cannot change)</span>
          <span>✏️ = Editing (can change)</span>
          <span>🏪 = Retail baseline (supermarket prices)</span>
          <span>👤 = Your individual volume discounts</span>
          <span>👥 = Kin group discounts</span>
          <span>✅ = Final order</span>
        </div>
      </div>
    </div>
  );
};

export default DraftEvolutionView;
